import React, { useEffect, useState } from 'react';
import { Guide, getGuides } from '../services/guidesService';
import { createPixPayment } from '../services/commerceService';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../services/firebase';

interface MaterialsPageProps {
  onBack: () => void;
  purchasedItems: string[];
  refreshUser: () => Promise<void>;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

const MaterialsPage: React.FC<MaterialsPageProps> = ({ onBack, purchasedItems, refreshUser, showToast }) => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string>('');
  const [paying, setPaying] = useState<string>('');
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [guideContentMode, setGuideContentMode] = useState<'video' | 'preview' | 'pdf'>('preview');
  const [isContentLoading, setIsContentLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const list = await getGuides();
        setGuides(list);
      } catch (err) {
        console.error(err);
        showToast('error', 'Falha ao carregar materiais');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [showToast]);

  const handleDownload = async (guide: Guide) => {
    if (!guide.ebookUrl) {
      showToast('error', 'Arquivo não disponível.');
      return;
    }
    setDownloading(guide.id);
    try {
      const url = await getDownloadURL(ref(storage, guide.ebookUrl));
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      showToast('error', 'Erro ao obter link de download');
    } finally {
      setDownloading('');
    }
  };

  const handleBuy = async (guide: Guide) => {
    setPaying(guide.id);
    try {
      const priceNumber = Number(String(guide.price || '').replace(/[^0-9.,]/g, '').replace(',', '.')) || 9.9;
      const payment = await createPixPayment(guide.id, priceNumber, `Material: ${guide.title}`);
      showToast('success', 'Pix gerado. Abra o QR ou link para pagar.');
      window.open(payment.ticket_url || payment.qr_code, '_blank');
      // Esperar webhook liberar; recarrega dados do usuário depois de alguns segundos
      setTimeout(() => refreshUser(), 4000);
    } catch (err: any) {
      console.error(err);
      showToast('error', err.message || 'Erro ao iniciar pagamento');
    } finally {
      setPaying('');
    }
  };

  const resolveUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
    const base = bucket?.includes('firebasestorage')
      ? `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/`
      : '';
    const path = encodeURIComponent(url.replace(/^\//, ''));
    return base ? `${base}${path}?alt=media` : `/${decodeURIComponent(path)}`;
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="animate-pulse h-6 bg-emerald-100 rounded w-40 mb-4" />
        <div className="grid sm:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="rounded-3xl bg-white shadow-lg border border-gray-100 p-6 space-y-4">
              <div className="h-4 bg-gray-100 rounded w-1/2" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
              <div className="h-32 bg-gray-50 rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white pb-12">
      <header className="bg-white/80 backdrop-blur border-b border-emerald-50 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-4">
          <div>
            <p className="text-sm text-emerald-600 font-semibold">Materiais Premium</p>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-black text-gray-900">Meus Guias</h1>
              <span className="inline-flex items-center gap-2 text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-bold border border-emerald-200">
                {guides.length} guias
              </span>
              <span className="inline-flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-bold border border-blue-100">
                {guides.filter((g) => purchasedItems?.includes(g.id)).length} liberados
              </span>
            </div>
          </div>
          <button
            onClick={onBack}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-emerald-50 transition"
          >
            ← Voltar
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {guides.length === 0 && (
          <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm text-center text-gray-500">
            Nenhum material cadastrado ainda.
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-6">
          {guides.map((guide) => {
            const unlocked = purchasedItems?.includes(guide.id);
            return (
              <div key={guide.id} className="bg-white rounded-3xl shadow-xl border border-gray-100 p-5 flex flex-col gap-4 hover:-translate-y-1 transition-transform">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-2xl">📘</div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.18em]">Guia Estratégico</p>
                    <h3 className="text-lg font-black text-gray-900 leading-tight">{guide.title}</h3>
                    {guide.tag && <p className="text-sm text-emerald-600 font-semibold">{guide.tag}</p>}
                  </div>
                </div>
                {guide.thumb && <img src={guide.thumb} alt={guide.title} className="rounded-2xl object-cover w-full h-36" />}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Guia digital</p>
                    <p className="text-xl font-black text-emerald-600">{guide.price || 'R$ 9,90'}</p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-bold border ${unlocked ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                    {unlocked ? 'Liberado' : 'Bloqueado'}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {guide.videoUrl && (
                    <button
                      onClick={() => {
                        setSelectedGuide(guide);
                        setGuideContentMode('video');
                        setIsContentLoading(true);
                      }}
                      className="w-full bg-white border border-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50"
                    >
                      Assistir vídeo
                    </button>
                  )}
                  {(guide.previewUrl || guide.ebookUrl) && (
                    <button
                      onClick={() => {
                        setSelectedGuide(guide);
                        setGuideContentMode('preview');
                        setIsContentLoading(true);
                      }}
                      className="w-full bg-white border border-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition hover:bg-emerald-50"
                    >
                      Ver amostra
                    </button>
                  )}
                  {unlocked ? (
                    <button
                      onClick={() => handleDownload(guide)}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl shadow-emerald-500/20 shadow-lg disabled:opacity-60"
                      disabled={downloading === guide.id}
                    >
                      {downloading === guide.id ? 'Gerando link...' : 'Baixar guia'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleBuy(guide)}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black py-3 rounded-2xl shadow-lg shadow-emerald-500/20 disabled:opacity-60"
                      disabled={paying === guide.id}
                    >
                      {paying === guide.id ? 'Gerando Pix...' : 'Comprar guia'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {selectedGuide && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4"
          onClick={() => setSelectedGuide(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl md:w-[90vw] max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full h-[70vh] sm:h-[75vh] bg-black">
              {(() => {
                const commonProps = {
                  className: "w-full h-full",
                  onLoad: () => setIsContentLoading(false),
                  loading: "lazy" as const,
                } as const;

                if (guideContentMode === 'video' && selectedGuide.videoUrl) {
                  const buildVideoSrc = (url: string) => {
                    if (!url) return '';
                    if (url.includes('youtube.com/embed')) {
                      return url.includes('?') ? `${url}&autoplay=1&mute=1` : `${url}?autoplay=1&mute=1`;
                    }
                    return url;
                  };
                  return (
                    <iframe
                      src={buildVideoSrc(selectedGuide.videoUrl)}
                      title={selectedGuide.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      {...commonProps}
                    />
                  );
                }

                if (guideContentMode === 'preview' && (selectedGuide.previewUrl || selectedGuide.ebookUrl)) {
                  const url = selectedGuide.previewUrl || selectedGuide.ebookUrl;
                  return (
                    <iframe
                      src={resolveUrl(url)}
                      title={`${selectedGuide.title} - Prévia`}
                      {...commonProps}
                    />
                  );
                }

                if (guideContentMode === 'pdf' && selectedGuide.ebookUrl) {
                  return (
                    <iframe
                      src={resolveUrl(selectedGuide.ebookUrl)}
                      title={selectedGuide.title}
                      {...commonProps}
                    />
                  );
                }

                setIsContentLoading(false);
                return (
                  <div className="w-full h-full flex items-center justify-center text-white text-sm">
                    Conteúdo indisponível
                  </div>
                );
              })()}
            </div>
            <div className="p-6 space-y-3 overflow-y-auto">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-sm font-bold text-emerald-600">Guia Estratégico</p>
                  <h3 className="text-2xl font-black text-gray-900">{selectedGuide.title}</h3>
                  {selectedGuide.tag && <p className="text-sm text-emerald-600 font-semibold">{selectedGuide.tag}</p>}
                </div>
                <span className="text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-black border border-emerald-200">
                  {selectedGuide.price || 'R$ 9,90'}
                </span>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {selectedGuide.videoUrl && (
                  <button
                    onClick={() => setGuideContentMode('video')}
                    className={`w-full font-bold py-3 rounded-2xl border transition ${guideContentMode === 'video' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-emerald-50'}`}
                  >
                    Assistir vídeo
                  </button>
                )}
                {(selectedGuide.previewUrl || selectedGuide.ebookUrl) && (
                  <button
                    onClick={() => setGuideContentMode('preview')}
                    className={`w-full font-bold py-3 rounded-2xl border transition ${guideContentMode === 'preview' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-emerald-50'}`}
                  >
                    Ver amostra
                  </button>
                )}
                {purchasedItems?.includes(selectedGuide.id) && selectedGuide.ebookUrl && (
                  <button
                    onClick={() => setGuideContentMode('pdf')}
                    className={`w-full font-bold py-3 rounded-2xl border transition ${guideContentMode === 'pdf' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-emerald-50'}`}
                  >
                    Abrir guia completo
                  </button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {purchasedItems?.includes(selectedGuide.id) && selectedGuide.ebookUrl ? (
                  <button
                    onClick={() => handleDownload(selectedGuide)}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl shadow-emerald-500/20 shadow-lg disabled:opacity-60"
                    disabled={downloading === selectedGuide.id}
                  >
                    {downloading === selectedGuide.id ? 'Gerando link...' : 'Baixar guia'}
                  </button>
                ) : selectedGuide.checkoutUrl ? (
                  <button
                    onClick={() => window.open(selectedGuide.checkoutUrl!, '_blank')}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black py-3 rounded-2xl shadow-lg shadow-emerald-500/20"
                  >
                    Comprar guia
                  </button>
                ) : (
                  <button
                    onClick={() => handleBuy(selectedGuide)}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black py-3 rounded-2xl shadow-lg shadow-emerald-500/20 disabled:opacity-60"
                    disabled={paying === selectedGuide.id}
                  >
                    {paying === selectedGuide.id ? 'Gerando Pix...' : 'Comprar guia'}
                  </button>
                )}
                <button
                  onClick={() => setSelectedGuide(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-2xl transition"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsPage;
